import 'data-layer-helper/dist/data-layer-helper';
import { buildEventParams } from './eventParams.js';
import { sanitizeEvent } from './firebaseSanitizer.js';
import { logEvent, setUserProperty } from './firebaseBridge.js';

/**
 * Properties, die Firebase nicht als Event-Parameter, sondern als User Property
 * erwartet. Werden vor dem Versand aus den Event-Parametern entfernt.
 */
const USER_PROPERTY_KEYS = ['user_id'];

/**
 * Liest GA4-Events aus `window.ga4DataLayer` und übergibt sie an das Firebase-SDK
 * der nativen App. Wird nur in der App-Webview verwendet, wo die native Bridge
 * verfügbar ist — im Webshop läuft stattdessen der `GA4Forwarder`.
 */
class FirebaseWebviewForwarder {
    static #initialized = false;

    constructor() {
        this.sentUserProperties = {};

        this._initEventListener();
    }

    static initOnce() {
        if (FirebaseWebviewForwarder.#initialized) {
            return;
        }
        FirebaseWebviewForwarder.#initialized = true;
        new FirebaseWebviewForwarder();
    }

    static _resetForTests() {
        FirebaseWebviewForwarder.#initialized = false;
    }

    _initEventListener() {
        if (typeof window === 'undefined' || typeof window.DataLayerHelper === 'undefined') {
            return;
        }
        window.ga4DataLayer = window.ga4DataLayer || [];
        new window.DataLayerHelper(window.ga4DataLayer, this._handleGa4Message.bind(this), true);
    }

    _handleGa4Message(model, message) {
        if (!message || typeof message.event !== 'string') {
            return;
        }
        this._send(message);
    }

    _send(eventObject) {
        const { eventName, params } = buildEventParams(eventObject);

        const eventParams = {
            ...params,
            page_location: window.location.href,
            page_referrer: document.referrer,
            page_title: document.title
        };

        this._forwardUserProperties(eventParams);

        const sanitized = sanitizeEvent(eventName, eventParams);
        if (!sanitized) {
            console.warn('[FirebaseWebviewForwarder] Event name is not usable in Firebase, skipping event', eventName);
            return;
        }

        logEvent(sanitized.name, sanitized.params);
    }

    /**
     * Zieht User Properties aus den Event-Parametern und schickt sie separat.
     * Unveränderte Werte werden nicht erneut gesendet.
     * @private
     */
    _forwardUserProperties(eventParams) {
        for (const key of USER_PROPERTY_KEYS) {
            if (!(key in eventParams)) {
                continue;
            }

            const value = eventParams[key];
            delete eventParams[key];

            if (value === null || value === undefined) {
                continue;
            }
            if (this.sentUserProperties[key] === value) {
                continue;
            }

            this.sentUserProperties[key] = value;
            setUserProperty(key, String(value));
        }
    }
}

export { FirebaseWebviewForwarder };
